/**
 * 802.11 Information Element Decoder
 *
 * Parses the raw IE hex string from WiFi scan results into structured,
 * human-readable data for display and DFP threat assessment.
 *
 * IE format from Android: "ID:HexData;ID:HexData;..."
 */

// ─── Types ───────────────────────────────────────────────────────

export interface DecodedIE {
  id: number;
  name: string;
  rawHex: string;
  decoded: Record<string, any>;
}

export interface WiFiSecurityInfo {
  protocol: string;          // "WPA3-SAE", "WPA2-PSK", "WPA3/WPA2 Mixed", "Open", etc.
  groupCipher: string;
  pairwiseCiphers: string[];
  akmSuites: string[];
  pmfRequired: boolean;
  pmfCapable: boolean;
}

export interface WiFiCapabilities {
  supports11n: boolean;      // HT (802.11n)
  supports11ac: boolean;     // VHT (802.11ac / WiFi 5)
  supports11ax: boolean;     // HE (802.11ax / WiFi 6)
  supports11be: boolean;     // EHT (802.11be / WiFi 7)
  wifiGeneration: string;    // "WiFi 4" / "WiFi 5" / "WiFi 6" / "WiFi 7"
  maxStreams: number;
  channelWidth: string;      // "20 MHz", "40 MHz", "80 MHz", "160 MHz"
}

export interface WiFiThreatIndicators {
  isHiddenSSID: boolean;
  isForeignCountry: boolean;
  countryCode: string;
  highTransmitPower: boolean;
  transmitPowerDbm: number | null;
  openNetwork: boolean;
  downgradeRisk: boolean;    // supports WPA2-PSK alongside WPA3 (downgrade attack surface)
  wmm: boolean;
  unusualVendorOUIs: string[];
  threatScore: number;       // 0-100, higher = more suspicious
  threatFlags: string[];
}

export interface DecodedWiFiIE {
  raw: string;
  elements: DecodedIE[];
  channel: number | null;
  countryCode: string;
  security: WiFiSecurityInfo;
  capabilities: WiFiCapabilities;
  threats: WiFiThreatIndicators;
  transmitPowerDbm: number | null;
  stationCount: number | null;
  channelUtilization: number | null;
  vendorInfo: { oui: string; name: string; data: string }[];
  isHiddenSSID: boolean;
}

// ─── OUI cipher/AKM lookups ─────────────────────────────────────

const CIPHER_SUITES: Record<string, string> = {
  '000fac00': 'Group (use group cipher)',
  '000fac01': 'WEP-40',
  '000fac02': 'TKIP',
  '000fac04': 'CCMP-128 (AES)',
  '000fac05': 'WEP-104',
  '000fac06': 'BIP-CMAC-128',
  '000fac08': 'GCMP-128',
  '000fac09': 'GCMP-256',
  '000fac0a': 'CCMP-256',
  '000fac0b': 'BIP-GMAC-128',
  '000fac0c': 'BIP-GMAC-256',
  '000fac0d': 'BIP-CMAC-256',
};

const AKM_SUITES: Record<string, string> = {
  '000fac01': '802.1X (EAP)',
  '000fac02': 'PSK',
  '000fac03': 'FT over 802.1X',
  '000fac04': 'FT over PSK',
  '000fac05': '802.1X (SHA-256)',
  '000fac06': 'PSK (SHA-256)',
  '000fac08': 'SAE (WPA3)',
  '000fac09': 'FT over SAE',
  '000fac0c': 'EAP-SIM (Passpoint)',
  '000fac0d': 'EAP-AKA (Passpoint)',
  '000fac0e': 'FILS-SHA256',
  '000fac0f': 'FILS-SHA384',
  '000fac12': 'OWE',
  '000fac18': 'SAE (H2E)',
};

const VENDOR_OUIS: Record<string, string> = {
  '0050f2': 'Microsoft (WMM/WPS)',
  '00904c': 'Broadcom',
  '001018': 'Broadcom',
  '506f9a': 'Wi-Fi Alliance',
  '00a0c6': 'Qualcomm',
  'f832e4': 'ASUSTek',
  '8cfdf0': 'Qualcomm',
  '0017f2': 'Apple',
};

const IE_NAMES: Record<number, string> = {
  0: 'SSID',
  1: 'Supported Rates',
  3: 'DS Parameter Set (Channel)',
  5: 'TIM',
  7: 'Country',
  11: 'QBSS Load',
  32: 'Power Constraint',
  33: 'Power Capability',
  35: 'TPC Report',
  42: 'ERP Information',
  45: 'HT Capabilities',
  48: 'RSN (Security)',
  50: 'Extended Supported Rates',
  51: 'AP Channel Report',
  59: 'Supported Operating Classes',
  61: 'HT Operation',
  70: 'RM Enabled Capabilities',
  74: 'Overlapping BSS Scan Parameters',
  127: 'Extended Capabilities',
  191: 'VHT Capabilities',
  192: 'VHT Operation',
  195: 'VHT TX Power Envelope',
  201: 'HE Capabilities (estimated)',
  221: 'Vendor Specific',
  244: 'EHT Operation (WiFi 7)',
  255: 'Extension Element',
};

// ─── Hex helpers ─────────────────────────────────────────────────

function hexToBytes(hex: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substring(i, i + 2), 16));
  }
  return bytes;
}

function hexToAscii(hex: string): string {
  let str = '';
  for (let i = 0; i < hex.length; i += 2) {
    const code = parseInt(hex.substring(i, i + 2), 16);
    if (code >= 32 && code < 127) str += String.fromCharCode(code);
  }
  return str;
}

// ─── Individual IE decoders ──────────────────────────────────────

function decodeSSID(hex: string): { ssid: string; hidden: boolean } {
  if (!hex || hex.length === 0) return { ssid: '', hidden: true };
  const ssid = hexToAscii(hex);
  return { ssid, hidden: ssid.length === 0 };
}

function decodeChannel(hex: string): number {
  return parseInt(hex, 16);
}

function decodeCountry(hex: string): { code: string; environment: string; channels: string } {
  const code = hexToAscii(hex.substring(0, 4));
  const env = hex.substring(4, 6);
  const envMap: Record<string, string> = { '20': 'Any', '49': 'Indoor', '4f': 'Outdoor' };
  const channels: string[] = [];
  for (let i = 6; i + 6 <= hex.length; i += 6) {
    const first = parseInt(hex.substring(i, i + 2), 16);
    const count = parseInt(hex.substring(i + 2, i + 4), 16);
    const maxPower = parseInt(hex.substring(i + 4, i + 6), 16);
    channels.push(`Ch ${first}-${first + count - 1} (${maxPower} dBm)`);
  }
  return { code, environment: envMap[env] || env, channels: channels.join(', ') };
}

function decodeTPCReport(hex: string): { txPower: number; linkMargin: number } {
  const bytes = hexToBytes(hex);
  return { txPower: bytes[0] || 0, linkMargin: bytes[1] || 0 };
}

function decodeQBSSLoad(hex: string): { stationCount: number; utilization: number; admissionCapacity: number } {
  const bytes = hexToBytes(hex);
  const stationCount = (bytes[1] << 8) | bytes[0];
  const utilization = Math.round((bytes[2] / 255) * 100);
  const admissionCapacity = (bytes[4] << 8) | bytes[3];
  return { stationCount, utilization, admissionCapacity };
}

function decodeRSN(hex: string): WiFiSecurityInfo {
  const result: WiFiSecurityInfo = {
    protocol: 'Unknown',
    groupCipher: 'Unknown',
    pairwiseCiphers: [],
    akmSuites: [],
    pmfRequired: false,
    pmfCapable: false,
  };

  try {
    let pos = 0;
    pos += 4; // version (2 bytes LE)

    // Group cipher suite (4 bytes)
    const groupOUI = hex.substring(pos, pos + 8).toLowerCase();
    result.groupCipher = CIPHER_SUITES[groupOUI] || groupOUI;
    pos += 8;

    // Pairwise cipher count (2 bytes LE)
    const pairCount = parseInt(hex.substring(pos + 2, pos + 4) + hex.substring(pos, pos + 2), 16);
    pos += 4;

    for (let i = 0; i < pairCount && pos + 8 <= hex.length; i++) {
      const suite = hex.substring(pos, pos + 8).toLowerCase();
      result.pairwiseCiphers.push(CIPHER_SUITES[suite] || suite);
      pos += 8;
    }

    // AKM count (2 bytes LE)
    if (pos + 4 <= hex.length) {
      const akmCount = parseInt(hex.substring(pos + 2, pos + 4) + hex.substring(pos, pos + 2), 16);
      pos += 4;

      for (let i = 0; i < akmCount && pos + 8 <= hex.length; i++) {
        const suite = hex.substring(pos, pos + 8).toLowerCase();
        result.akmSuites.push(AKM_SUITES[suite] || suite);
        pos += 8;
      }
    }

    // RSN Capabilities (2 bytes LE)
    if (pos + 4 <= hex.length) {
      const caps = parseInt(hex.substring(pos + 2, pos + 4) + hex.substring(pos, pos + 2), 16);
      result.pmfCapable = !!(caps & 0x80);
      result.pmfRequired = !!(caps & 0x40);
    }

    // Determine protocol name
    const hasSAE = result.akmSuites.some(s => s.includes('SAE'));
    const hasPSK = result.akmSuites.some(s => s.includes('PSK'));
    const hasEAP = result.akmSuites.some(s => s.includes('EAP') || s.includes('802.1X'));
    const hasOWE = result.akmSuites.some(s => s.includes('OWE'));

    if (hasSAE && hasPSK) result.protocol = 'WPA3/WPA2 Mixed';
    else if (hasSAE) result.protocol = 'WPA3-SAE';
    else if (hasOWE) result.protocol = 'WPA3-OWE (Enhanced Open)';
    else if (hasEAP) result.protocol = 'WPA2-Enterprise';
    else if (hasPSK) result.protocol = 'WPA2-PSK';
    else result.protocol = 'WPA2';
  } catch {
    // partial parse is fine
  }

  return result;
}

function decodeHTCapabilities(hex: string): { maxStreams: number; supports40MHz: boolean; shortGI20: boolean; shortGI40: boolean } {
  const bytes = hexToBytes(hex);
  const htCapInfo = (bytes[1] << 8) | bytes[0];
  return {
    supports40MHz: !!(htCapInfo & 0x0002),
    shortGI20: !!(htCapInfo & 0x0020),
    shortGI40: !!(htCapInfo & 0x0040),
    maxStreams: 1, // simplified; full decode requires MCS bitmask
  };
}

function decodeVHTCapabilities(hex: string): { supports80MHz: boolean; supports160MHz: boolean; maxStreams: number } {
  const bytes = hexToBytes(hex);
  const vhtCapInfo = (bytes[3] << 24) | (bytes[2] << 16) | (bytes[1] << 8) | bytes[0];
  const chanWidth = vhtCapInfo & 0x03;
  return {
    supports80MHz: true, // VHT always supports 80 MHz
    supports160MHz: chanWidth >= 1,
    maxStreams: 1, // simplified
  };
}

function decodeVHTOperation(hex: string): { channelWidth: number; centerFreq0: number; centerFreq1: number } {
  const bytes = hexToBytes(hex);
  return {
    channelWidth: bytes[0],
    centerFreq0: bytes[1],
    centerFreq1: bytes[2],
  };
}

function decodeVendorSpecific(hex: string): { oui: string; vendorName: string; subtype: number; data: string } {
  const oui = hex.substring(0, 6).toLowerCase();
  const vendorName = VENDOR_OUIS[oui] || `Unknown (${oui})`;
  const subtype = parseInt(hex.substring(6, 8), 16);
  return { oui, vendorName, subtype, data: hex.substring(8) };
}

// ─── Main decoder ────────────────────────────────────────────────

export function decodeWiFiIE(raw: string): DecodedWiFiIE {
  const result: DecodedWiFiIE = {
    raw,
    elements: [],
    channel: null,
    countryCode: '',
    security: { protocol: 'Open', groupCipher: '', pairwiseCiphers: [], akmSuites: [], pmfRequired: false, pmfCapable: false },
    capabilities: { supports11n: false, supports11ac: false, supports11ax: false, supports11be: false, wifiGeneration: 'WiFi 4', maxStreams: 1, channelWidth: '20 MHz' },
    threats: { isHiddenSSID: false, isForeignCountry: false, countryCode: '', highTransmitPower: false, transmitPowerDbm: null, openNetwork: false, downgradeRisk: false, wmm: false, unusualVendorOUIs: [], threatScore: 0, threatFlags: [] },
    transmitPowerDbm: null,
    stationCount: null,
    channelUtilization: null,
    vendorInfo: [],
    isHiddenSSID: false,
  };

  if (!raw) return result;

  const parts = raw.split(';').filter(Boolean);

  for (const part of parts) {
    const colonIdx = part.indexOf(':');
    if (colonIdx === -1) continue;

    const id = parseInt(part.substring(0, colonIdx), 10);
    const hex = part.substring(colonIdx + 1);
    if (isNaN(id)) continue;

    const ie: DecodedIE = { id, name: IE_NAMES[id] || `IE ${id}`, rawHex: hex, decoded: {} };

    try {
      switch (id) {
        case 0: {
          const ssid = decodeSSID(hex);
          ie.decoded = ssid;
          result.isHiddenSSID = ssid.hidden;
          break;
        }
        case 3: {
          result.channel = decodeChannel(hex);
          ie.decoded = { channel: result.channel };
          break;
        }
        case 7: {
          const country = decodeCountry(hex);
          result.countryCode = country.code;
          ie.decoded = country;
          break;
        }
        case 11: {
          const qbss = decodeQBSSLoad(hex);
          result.stationCount = qbss.stationCount;
          result.channelUtilization = qbss.utilization;
          ie.decoded = qbss;
          break;
        }
        case 35: {
          const tpc = decodeTPCReport(hex);
          result.transmitPowerDbm = tpc.txPower;
          ie.decoded = tpc;
          break;
        }
        case 45: {
          const ht = decodeHTCapabilities(hex);
          result.capabilities.supports11n = true;
          ie.decoded = ht;
          break;
        }
        case 48: {
          result.security = decodeRSN(hex);
          ie.decoded = result.security;
          break;
        }
        case 191: {
          const vht = decodeVHTCapabilities(hex);
          result.capabilities.supports11ac = true;
          ie.decoded = vht;
          break;
        }
        case 192: {
          const vhtOp = decodeVHTOperation(hex);
          ie.decoded = vhtOp;
          if (vhtOp.channelWidth >= 1) result.capabilities.channelWidth = '80 MHz';
          if (vhtOp.channelWidth >= 2) result.capabilities.channelWidth = '160 MHz';
          break;
        }
        case 201:
        case 255: {
          // HE (WiFi 6) or EHT (WiFi 7) extension
          result.capabilities.supports11ax = true;
          ie.decoded = { description: 'HE/WiFi 6 capability element' };
          break;
        }
        case 244: {
          result.capabilities.supports11be = true;
          ie.decoded = { description: 'EHT/WiFi 7 operation element' };
          break;
        }
        case 221: {
          const vendor = decodeVendorSpecific(hex);
          ie.decoded = vendor;
          result.vendorInfo.push({ oui: vendor.oui, name: vendor.vendorName, data: hex });
          if (vendor.oui === '0050f2' && vendor.subtype === 2) {
            result.threats.wmm = true;
          }
          break;
        }
        default:
          ie.decoded = { hex };
      }
    } catch {
      ie.decoded = { hex, parseError: true };
    }

    result.elements.push(ie);
  }

  // Determine WiFi generation
  if (result.capabilities.supports11be) {
    result.capabilities.wifiGeneration = 'WiFi 7';
    result.capabilities.channelWidth = '320 MHz';
  } else if (result.capabilities.supports11ax) {
    result.capabilities.wifiGeneration = 'WiFi 6';
    if (!result.capabilities.supports11ac) result.capabilities.channelWidth = '80 MHz';
  } else if (result.capabilities.supports11ac) {
    result.capabilities.wifiGeneration = 'WiFi 5';
  } else if (result.capabilities.supports11n) {
    result.capabilities.wifiGeneration = 'WiFi 4';
  } else {
    result.capabilities.wifiGeneration = 'Legacy';
  }

  // ─── Threat Assessment ────────────────────────────────────────
  const flags: string[] = [];
  let score = 0;

  // Hidden SSID
  if (result.isHiddenSSID) {
    result.threats.isHiddenSSID = true;
    flags.push('Hidden SSID');
    score += 15;
  }

  // Foreign country
  const cc = result.countryCode.trim().toUpperCase();
  result.threats.countryCode = cc;
  if (cc && cc !== 'US' && cc !== '') {
    result.threats.isForeignCountry = true;
    flags.push(`Foreign Country: ${cc}`);
    score += 35;
  }

  // High transmit power (>24 dBm is unusual for consumer APs)
  if (result.transmitPowerDbm !== null) {
    result.threats.transmitPowerDbm = result.transmitPowerDbm;
    if (result.transmitPowerDbm > 24) {
      result.threats.highTransmitPower = true;
      flags.push(`High TX Power: ${result.transmitPowerDbm} dBm`);
      score += 20;
    }
  }

  // Open network (no RSN IE or explicitly open)
  const hasRSN = result.elements.some(e => e.id === 48);
  if (!hasRSN) {
    result.threats.openNetwork = true;
    result.security.protocol = 'Open';
    flags.push('Open Network (No Encryption)');
    score += 25;
  }

  // WPA3/WPA2 mixed = downgrade attack surface
  if (result.security.protocol === 'WPA3/WPA2 Mixed') {
    result.threats.downgradeRisk = true;
    flags.push('Downgrade Attack Surface (WPA3/WPA2 Mixed)');
    score += 10;
  }

  // Unknown vendor OUIs in vendor-specific IEs
  const unknownVendors = result.vendorInfo.filter(v => v.name.startsWith('Unknown'));
  if (unknownVendors.length > 0) {
    result.threats.unusualVendorOUIs = unknownVendors.map(v => v.oui);
    if (unknownVendors.length > 2) {
      flags.push(`${unknownVendors.length} unknown vendor IEs`);
      score += 10;
    }
  }

  result.threats.threatScore = Math.min(score, 100);
  result.threats.threatFlags = flags;

  return result;
}

// ─── Quick summary for table display ─────────────────────────────

export function getIESummaryChips(decoded: DecodedWiFiIE): { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' }[] {
  const chips: { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' }[] = [];

  if (decoded.capabilities.wifiGeneration) {
    chips.push({ label: decoded.capabilities.wifiGeneration, color: 'info' });
  }
  if (decoded.channel) {
    chips.push({ label: `Ch ${decoded.channel}`, color: 'default' });
  }
  if (decoded.security.protocol !== 'Open') {
    chips.push({ label: decoded.security.protocol, color: 'success' });
  }
  if (decoded.countryCode) {
    chips.push({ label: decoded.countryCode, color: decoded.threats.isForeignCountry ? 'error' : 'default' });
  }
  if (decoded.isHiddenSSID) {
    chips.push({ label: 'Hidden', color: 'warning' });
  }
  if (decoded.threats.openNetwork) {
    chips.push({ label: 'Open', color: 'error' });
  }
  if (decoded.threats.highTransmitPower) {
    chips.push({ label: `${decoded.transmitPowerDbm} dBm`, color: 'warning' });
  }
  if (decoded.threats.threatScore >= 40) {
    chips.push({ label: `Threat: ${decoded.threats.threatScore}`, color: 'error' });
  }

  return chips;
}
